import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { ArrowRight, Clock, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RatgeberPage = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog`).then(res => setArticles(res.data)).catch(console.error);
  }, []);

  const categories = ["Alle", "Ratgeber", "Recht", "Finanzen"];
  const [activeCategory, setActiveCategory] = useState("Alle");

  const filteredArticles = activeCategory === "Alle" 
    ? articles 
    : articles.filter(a => a.category === activeCategory);

  return (
    <>
      <SEO 
        title="Ratgeber - Wissen rund um Kfz-Gutachten"
        description="Ratgeber zu Kfz-Gutachten: Kosten, Wertminderung, fiktive Abrechnung, Ihre Rechte nach einem Unfall. Praktische Tipps vom unabhängigen Sachverständigen."
        canonical="/ratgeber"
        keywords="Kfz Gutachten Ratgeber, Unfallgutachten Kosten, Wertminderung, fiktive Abrechnung, Rechte nach Unfall"
      />
      {/* Hero */}
      <section className="bg-[#F7F7F7] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Ratgeber</span>
            <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">
              Wissen rund um Kfz-Gutachten
            </h1>
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Informieren Sie sich über Ihre Rechte nach einem Unfall, erfahren Sie alles über die Erstellung von Gutachten und lernen Sie, wie Sie Ihre Ansprüche optimal durchsetzen.
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Articles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-sm font-medium transition-colors ${
                  activeCategory === cat
                    ? "bg-[#AA6622] text-white"
                    : "bg-[#F7F7F7] text-[#4A4A4A] hover:text-black"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/ratgeber/${article.slug}`}
                className="bg-[#F7F7F7] rounded-sm overflow-hidden card-hover group"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 text-sm text-[#4A4A4A] mb-3">
                    <span className="bg-[#AA6622]/10 text-[#AA6622] px-2 py-1 rounded-sm font-medium">
                      {article.category}
                    </span>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{article.read_time}</span>
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-black mb-3 font-['Outfit'] group-hover:text-[#AA6622] transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-[#4A4A4A] leading-relaxed mb-4">{article.excerpt}</p>
                  <span className="inline-flex items-center text-[#AA6622] font-bold">
                    <span>Weiterlesen</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#4A4A4A]">Keine Artikel in dieser Kategorie gefunden.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter / CTA */}
      <section className="py-20 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Haben Sie Fragen?</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">
            Wenn Sie Fragen zu einem bestimmten Thema haben oder eine individuelle Beratung wünschen, kontaktieren Sie uns gerne.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center space-x-2 bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold hover:bg-[#8e541b] transition-colors"
          >
            <span>Kontakt aufnehmen</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default RatgeberPage;
